import { CSSProperties, FC, useMemo } from 'react';
import styled from 'styled-components';

import { ParticipantPosition } from '@typings/participant';
import { SHOW_CARD_WIDTH } from '@utils/constants';
import { PremierText } from '@components/text';

const SIDE_HEIGHT = 24;
const SKEW_ANGLE = 16;

export const GradientTextContainer = styled.div<{
  $width: number | null;
  $height?: number;
}>`
  position: relative;
  width: ${({ $width }) => ($width ? `${$width}px` : '100%')};
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

export const SideBar = styled.div<{ $height?: number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${({ $height }) => ($height ? `${$height}px` : '100%')};
  display: flex;
  flex-direction: row;
  overflow: hidden;
  z-index: 1;
`;

export const GradientSkewedContainer = styled.div<{
  $position: ParticipantPosition;
  $width?: number;
  $isLeft?: boolean;
  $widthPercent?: number;
}>`
  position: relative;
  width: ${({ $width, $widthPercent }) =>
    $widthPercent !== undefined
      ? `${$widthPercent}%`
      : $width
        ? `${$width}px`
        : '100%'};
  ${({ $isLeft }) => ($isLeft ? 'left: 0;' : 'right: 0;')};
  background: ${({ $isLeft, theme }) =>
    $isLeft
      ? `linear-gradient(90deg, ${theme.colors.blue[500]} 60%, ${theme.colors.purple[500]} 100%)`
      : `linear-gradient(90deg, ${theme.colors.purple[500]} 0%, ${theme.colors.red[500]} 60%)`};
  transform: skew(-${SKEW_ANGLE}deg, 0);
  transform-origin: ${({ $isLeft }) => ($isLeft ? 'top' : 'bottom')};
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: ${({ $widthPercent }) =>
      $widthPercent !== undefined ? `${$widthPercent}%` : '50%'};
  }
`;

export const SideTextContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  z-index: 2;
`;

export const SideText = styled(PremierText)<{ $isLeft?: boolean }>`
  width: 50%;
  padding-left: ${({ $isLeft }) => ($isLeft ? '4px' : '8px')};
  padding-right: ${({ $isLeft }) => ($isLeft ? '8px' : '6px')};
  font-size: ${({ theme }) => theme.fonts.fontSize.sm};
  text-align: ${({ $isLeft }) => ($isLeft ? 'left' : 'right')};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  }
`;

type Props = {
  blueSide?: string;
  redSide?: string;
  isCard?: boolean;
  textStyle?: CSSProperties;
  leftVotes?: number;
  rightVotes?: number;
};

const calculateWidthPercentages = (leftVotes: number, rightVotes: number) => {
  const totalVotes = leftVotes + rightVotes;
  let leftWidthPercent = 50;
  let rightWidthPercent = 50;

  if (totalVotes > 0) {
    const rawLeftPercent = (leftVotes / totalVotes) * 100;
    leftWidthPercent = Math.max(20, Math.min(80, rawLeftPercent));
    rightWidthPercent = 100 - leftWidthPercent;
  }

  return { leftWidthPercent, rightWidthPercent };
};

const Sides: FC<Props> = ({
  blueSide,
  redSide,
  isCard = true,
  textStyle = {},
  leftVotes = 0,
  rightVotes = 0,
}) => {
  const width = isCard ? SHOW_CARD_WIDTH : 0;
  const height = isCard ? SIDE_HEIGHT : 0;
  const { leftWidthPercent, rightWidthPercent } = useMemo(
    () => calculateWidthPercentages(leftVotes, rightVotes),
    [leftVotes, rightVotes],
  );

  return (
    <GradientTextContainer $width={width} $height={height}>
      <SideBar>
        <GradientSkewedContainer
          $position={ParticipantPosition.Left}
          $width={width / 2}
          $widthPercent={leftWidthPercent}
          $isLeft
        />
        <GradientSkewedContainer
          $position={ParticipantPosition.Right}
          $width={width / 2}
          $widthPercent={rightWidthPercent}
        />
      </SideBar>

      <SideTextContainer>
        <SideText $isLeft style={textStyle}>
          {blueSide}
        </SideText>

        <SideText style={textStyle}>{redSide}</SideText>
      </SideTextContainer>
    </GradientTextContainer>
  );
};

export default Sides;
